import { Box, Input, Skeleton, Spinner, Text, VStack } from '@chakra-ui/react';
import { Mail, Search } from 'iconoir-react';
import { InputGroup } from '@/components/ui/input-group';
import { isValidEmail } from '@/lib/is-valid-email';
import { RecipientContactRow } from './RecipientContactRow';
import { RecipientEmailRow } from './RecipientEmailRow';
import type { RecipientOptionView } from './types';

interface RecipientPickerProps {
  /** Already-mapped options. The picker never fetches; the consumer injects these. */
  options: RecipientOptionView[];
  /** First load: renders skeletons. */
  isLoading?: boolean;
  /** Background refetch: dims the list and shows an in-field spinner. */
  isFetching?: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSelectContact: (id: string) => void;
  onSelectRawEmail: (email: string) => void;
  placeholder?: string;
  emptyHint?: string;
  /** Secondary label on the raw-email row (the type-specific CTA copy). */
  rawEmailActionLabel?: string;
}

/**
 * Presentational search-or-enter-email picker. It does NOT fetch: the consumer
 * injects `options` (already mapped to a neutral view) and drives the search
 * term. It emits either a chosen option id or a raw email, and stays ignorant of
 * what the recipient is for (collab, affiliation, ...). The Dialog/modal shell
 * lives in the consumer, so the same field can sit in a standalone dialog or
 * inline in a larger modal.
 */
export const RecipientPicker = ({
  options,
  isLoading = false,
  isFetching = false,
  searchValue,
  onSearchChange,
  onSelectContact,
  onSelectRawEmail,
  placeholder,
  emptyHint,
  rawEmailActionLabel,
}: RecipientPickerProps) => {
  const trimmed = searchValue.trim();
  const isEmail = isValidEmail(trimmed);
  // Never offer a raw-email row for an address a known contact already carries:
  // the resolved, identity-backed contact is always the better choice.
  const emailMatchesOption = options.some(
    (option) => option.email?.toLowerCase() === trimmed.toLowerCase()
  );
  const showEmailRow = isEmail && !emailMatchesOption;

  const showEmptyState = !isLoading && !isFetching && options.length === 0 && !showEmailRow;

  return (
    <>
      {/* Position relative so the in-flight spinner overlays the field's end
          without shifting layout as it appears/disappears. */}
      <Box position="relative" mb="12px">
        <InputGroup
          w="full"
          gapX={2}
          startElement={
            isEmail ? (
              <Mail width={16} height={16} color="gray" />
            ) : (
              <Search width={16} height={16} color="gray" />
            )
          }
        >
          <Input
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={placeholder}
            variant="outline"
            rounded="md"
          />
        </InputGroup>
        {!isLoading && isFetching && (
          // Center via a full-height flex wrapper, not `transform` on the Spinner:
          // its spin animation drives `transform: rotate()` and would clobber a
          // static translateY, pushing it off-center.
          <Box
            position="absolute"
            right="10px"
            top="0"
            bottom="0"
            display="flex"
            alignItems="center"
          >
            <Spinner size="xs" color="fg.muted" />
          </Box>
        )}
      </Box>

      {isLoading ? (
        <VStack gap="6px" align="stretch">
          <Skeleton height="52px" rounded="10px" />
          <Skeleton height="52px" rounded="10px" />
          <Skeleton height="52px" rounded="10px" />
        </VStack>
      ) : showEmptyState ? (
        <Text fontSize="sm" color="fg.muted" textAlign="center" py="24px">
          {emptyHint}
        </Text>
      ) : (
        // Keep the previous list visible (keepPreviousData) but dim it while a new
        // search is in flight — a discreet hint, no flicker, no blocked typing.
        <VStack
          gap="2px"
          align="stretch"
          maxH="320px"
          overflowY="auto"
          opacity={isFetching ? 0.55 : 1}
          transition="opacity 0.15s"
        >
          {showEmailRow && (
            <RecipientEmailRow
              email={trimmed}
              actionLabel={rawEmailActionLabel}
              onSelect={onSelectRawEmail}
            />
          )}
          {options.map((option) => (
            <RecipientContactRow key={option.id} option={option} onSelect={onSelectContact} />
          ))}
        </VStack>
      )}
    </>
  );
};
